import { Injectable } from '@angular/core'
import { v4 as uuidv4 } from 'uuid'
import { LoggingService } from '../../../services/logging.service'
import type { FormSubmissionStatus, PaymentFormStatus } from '../../../utilities/form-status'
import type {
    VenueStorefrontBookingFlowState,
    VenueStorefrontSearchChange,
    VenueStorefrontAvailabilityStatus,
} from './venue-storefront-booking-flow.types'

export type StorefrontBookingEvent =
    | `search_${VenueStorefrontSearchChange['field']}_changed`
    | `search_availability_${VenueStorefrontAvailabilityStatus}`
    | `step_${VenueStorefrontBookingFlowState['step'] | 'enquiry'}_viewed`
    | `${'details' | 'waitlist' | 'enquiry'}_submission_${FormSubmissionStatus}`
    | `payment_form_${PaymentFormStatus}`
    | 'flow_started' | 'availability_requested' | 'availability_loaded'
    | 'availability_failed' | 'choice_viewed' | 'choice_dismissed' | 'reason_selected'
    | 'event_selected' | 'standard_selected' | 'slot_selected' | 'waitlist_selected' | 'extra_changed'
    | 'extras_confirmed' | 'policy_accepted' | 'details_continued'
    | 'payment_preparation_started' | 'payment_preparation_failed' | 'payment_requirement_failed'
    | 'payment_confirmation_started' | 'payment_confirmed'
    | 'booking_submitted' | 'booking_submission_failed' | 'booking_confirmed' | 'confirmation_failed'
    | 'navigated_back' | 'returned_to_search' | 'returned_to_venue'

export interface StorefrontBookingContext {
    reasonId?: string
    reasonName?: string
    eventId?: string
    eventName?: string
    areaId?: string
    partySize?: number
    slotTime?: string
    durationMinutes?: number
}

interface StorefrontTrackingContext extends StorefrontBookingContext {
    selectionField?: VenueStorefrontSearchChange['field'] | 'availability'
    selectionValue?: string | number
    productId?: string
    skuId?: string
    quantity?: number
    itemCount?: number
    fromStep?: string
    toStep?: string
    form?: 'details' | 'waitlist' | 'enquiry'
    status?: FormSubmissionStatus | PaymentFormStatus
    httpStatus?: number
}

interface StorefrontTrackingScope {
    organisationId: string
    businessId: string
    venueId: string
}

type TrackingStep = VenueStorefrontBookingFlowState['step'] | 'enquiry'

// Provided by the storefront component: each booking flow gets its own context.
@Injectable()
export class VenueStorefrontBookingTracking {
    private flowId = uuidv4()
    private eventSequence = 0
    private context: StorefrontBookingContext = {}
    private scope: StorefrontTrackingScope | null = null
    private step: TrackingStep = 'search'

    constructor(private loggingService: LoggingService) {}

    start(scope: StorefrontTrackingScope, partySize?: number) {
        this.scope = scope
        this.restart(partySize)
    }

    restart(partySize?: number) {
        this.flowId = uuidv4()
        this.eventSequence = 0
        this.step = 'search'
        this.context = { partySize }
        this.record('flow_started')
        this.record('step_search_viewed')
    }

    clearChoice() {
        const { partySize, slotTime, durationMinutes } = this.context
        this.context = { partySize, slotTime, durationMinutes }
    }

    transition(step: TrackingStep, context: StorefrontBookingContext) {
        const previousStep = this.step
        this.step = step
        this.context = step === 'waiting' || step === 'confirmation'
            ? { ...this.context, ...context }
            : context
        if (step !== previousStep) {
            this.record(`step_${step}_viewed`, { fromStep: previousStep })
        }
    }

    recordAvailabilityStatus(status: VenueStorefrontAvailabilityStatus) {
        this.record(`search_availability_${status}`, { selectionField: 'availability', selectionValue: status })
    }

    recordSearchInteraction(interaction: VenueStorefrontSearchChange) {
        this.record(`search_${interaction.field}_changed`, {
            selectionField: interaction.field,
            selectionValue: interaction.value,
        })
    }

    recordFormProgress(form: 'details' | 'waitlist' | 'enquiry', status: FormSubmissionStatus) {
        this.record(`${form}_submission_${status}`, { form, status })
    }

    recordPaymentProgress(status: PaymentFormStatus) {
        this.record(`payment_form_${status}`, { status })
    }

    // Explicit scalar allowlist: never spread state, requests, forms, errors or payment objects.
    // No global custom attributes: venue and choice context must not leak into another flow.
    record(event: StorefrontBookingEvent, context: StorefrontTrackingContext = {}) {
        if (!this.scope) {
            return
        }
        const attributes = {
            flow: 'venue_storefront_booking',
            flowId: this.flowId,
            sequence: ++this.eventSequence,
            step: this.step,
            organisationId: this.scope.organisationId,
            businessId: this.scope.businessId,
            venueId: this.scope.venueId,
            reasonId: context.reasonId ?? (context.eventId ? undefined : this.context.reasonId),
            reasonName: context.reasonName ?? (context.eventId ? undefined : this.context.reasonName),
            eventId: context.eventId ?? (context.reasonId ? undefined : this.context.eventId),
            eventName: context.eventName ?? (context.reasonId ? undefined : this.context.eventName),
            areaId: context.areaId ?? this.context.areaId,
            partySize: this.context.partySize,
            selectionField: context.selectionField,
            selectionValue: context.selectionValue,
            productId: context.productId,
            skuId: context.skuId,
            quantity: context.quantity,
            itemCount: context.itemCount,
            slotTime: context.slotTime ?? this.context.slotTime,
            durationMinutes: context.durationMinutes ?? this.context.durationMinutes,
            fromStep: context.fromStep,
            toStep: context.toStep,
            form: context.form,
            status: context.status,
            httpStatus: context.httpStatus,
        }
        this.loggingService.trackAction(`venue_storefront_${event.replaceAll('-', '_')}`,
            Object.fromEntries(Object.entries(attributes).filter(([, value]) => value !== undefined)))
    }
}
